/**
 * Floor Plan Diagnostics Tool - Analysis Only
 * 
 * This tool provides detailed analysis of floor plan integrity issues
 * to identify root causes and inform prevention strategies.
 * NO AUTOMATED REPAIRS - Analysis only for understanding problems.
 */

import React, { useState, useCallback } from 'react';
import type { WallWithTempId, Room } from '../types';
import { diagnoseFloorPlan, generateDiagnosticReport, type FloorPlanDiagnostics } from '../utils/floorPlanDiagnostics';

interface FloorPlanDiagnosticsOnlyProps {
  // Current floor plan data
  currentWalls: WallWithTempId[];
  allRooms: Room[];
  roomCustomLabels: Record<string, string>;
  currentFloor: number;
  floorPlans: Record<number, WallWithTempId[]>;
  
  // Optional debug callback
  onDebug?: (event: string, data: any) => void;
}

interface RootCauseAnalysis {
  category: string;
  rootCause: string;
  evidenceCount: number;
  affectedFloors: number[];
  preventionStrategy: string;
  urgency: 'low' | 'medium' | 'high' | 'critical';
}

export const FloorPlanDiagnosticsOnly: React.FC<FloorPlanDiagnosticsOnlyProps> = ({
  currentWalls,
  allRooms,
  roomCustomLabels,
  currentFloor,
  floorPlans,
  onDebug
}) => {
  const [allDiagnostics, setAllDiagnostics] = useState<FloorPlanDiagnostics[]>([]);
  const [rootCauseAnalysis, setRootCauseAnalysis] = useState<RootCauseAnalysis[]>([]);
  const [isRunningDiagnostics, setIsRunningDiagnostics] = useState(false);
  const [showDetailedReport, setShowDetailedReport] = useState(false);
  const [selectedFloorForAnalysis, setSelectedFloorForAnalysis] = useState<number | null>(null);

  const runFullSystemAnalysis = useCallback(async () => {
    setIsRunningDiagnostics(true);
    console.log('🔍 Running comprehensive floor plan analysis...');
    
    try {
      const diagnosticsResults: FloorPlanDiagnostics[] = [];
      
      // Get all floor numbers
      const floorNumbers = Object.keys(floorPlans).map(Number).sort();
      if (!floorNumbers.includes(currentFloor)) {
        floorNumbers.push(currentFloor);
      }
      
      console.log(`📊 Analyzing ${floorNumbers.length} floors: ${floorNumbers.join(', ')}`);
      
      // Run diagnostics on each floor
      for (const floorNumber of floorNumbers) {
        const wallsToAnalyze = floorNumber === currentFloor 
          ? currentWalls 
          : floorPlans[floorNumber] || [];
        
        // 🔧 CRITICAL FIX: Calculate floor-specific rooms instead of using all rooms
        // This prevents false room-wall relationship corruption between floors
        const { calculateRooms } = await import('../utils/floorPlanUtils');
        const floorSpecificRooms = calculateRooms(wallsToAnalyze, roomCustomLabels, 'explicitRoomCreation', floorNumber);
        
        console.log(`\n🏢 Floor ${floorNumber} Analysis:`);
        console.log(`   • Walls: ${wallsToAnalyze.length}`);
        console.log(`   • Floor-Specific Rooms: ${floorSpecificRooms.length}`);
        console.log(`   • Total Rooms (All Floors): ${allRooms.length}`);
        
        const floorDiagnostics = diagnoseFloorPlan(
          wallsToAnalyze,
          floorSpecificRooms, // Use floor-specific rooms to prevent cross-floor corruption
          roomCustomLabels,
          floorNumber
        );
        
        diagnosticsResults.push(floorDiagnostics);
        
        // Log individual floor results
        console.log(`   • Issues Found: ${floorDiagnostics.totalIssues} (${floorDiagnostics.criticalIssues} critical)`);
        console.log(`   • Extrusions: ${floorDiagnostics.summary.extrusions}`);
        console.log(`   • Connectors: ${floorDiagnostics.summary.connectors}`);
        console.log(`   • Duplicates: ${floorDiagnostics.summary.duplicateConnectors}`);
        console.log(`   • Orphaned: ${floorDiagnostics.summary.orphanedWalls}`);
      }
      
      setAllDiagnostics(diagnosticsResults);
      
      // Perform root cause analysis
      const rootCauses = analyzeRootCauses(diagnosticsResults);
      setRootCauseAnalysis(rootCauses);
      
      // Log comprehensive summary
      logComprehensiveSummary(diagnosticsResults, rootCauses);
      
      onDebug?.('FULL_ANALYSIS_COMPLETED', {
        floorsAnalyzed: diagnosticsResults.length,
        totalIssues: diagnosticsResults.reduce((sum, d) => sum + d.totalIssues, 0),
        totalCritical: diagnosticsResults.reduce((sum, d) => sum + d.criticalIssues, 0),
        rootCauseCount: rootCauses.length
      });
      
    } catch (error) {
      console.error('🚨 Analysis failed:', error);
      onDebug?.('ANALYSIS_FAILED', { error: error instanceof Error ? error.message : String(error) });
    } finally {
      setIsRunningDiagnostics(false);
    }
  }, [currentWalls, allRooms, roomCustomLabels, currentFloor, floorPlans, onDebug]);

  const analyzeRootCauses = (diagnostics: FloorPlanDiagnostics[]): RootCauseAnalysis[] => {
    const rootCauses: RootCauseAnalysis[] = [];
    
    // 1. EXTRUSION INTEGRITY ISSUES
    const extrusionIssues = diagnostics.flatMap(d => 
      d.results.filter(r => r.category === 'extrusion')
    );
    
    console.log('🔍 EXTRUSION_ISSUES_DEBUG', {
      totalExtrusionIssues: extrusionIssues.length,
      extrusionIssueDetails: extrusionIssues.map(issue => ({
        severity: issue.severity,
        category: issue.category,
        issue: issue.issue,
        details: issue.details
      })),
      floorsWithExtrusionIssues: diagnostics.map(d => ({
        floor: d.floorNumber,
        extrusionIssueCount: d.results.filter(r => r.category === 'extrusion').length
      }))
    });
    
    if (extrusionIssues.length > 0) {
      const orphanedMainSegments = extrusionIssues.filter(i => i.issue.includes('Orphaned main')).length;
      const orphanedConnectors = extrusionIssues.filter(i => i.issue.includes('Orphaned connector')).length;
      const tooManyConnectors = extrusionIssues.filter(i => i.issue.includes('Too many connector')).length;
      
      if (orphanedMainSegments > 0 || orphanedConnectors > 0) {
        rootCauses.push({
          category: 'Extrusion Metadata Corruption',
          rootCause: 'Parent-child relationships between main segments and connectors are being broken during resize/split operations',
          evidenceCount: orphanedMainSegments + orphanedConnectors,
          affectedFloors: diagnostics.filter(d => d.results.some(r => r.category === 'extrusion' && r.issue.includes('Orphaned'))).map(d => d.floorNumber),
          preventionStrategy: 'Implement robust metadata preservation during all extrusion operations. Add validation checks before/after resize operations.',
          urgency: 'critical'
        });
      }
      
      if (tooManyConnectors > 0) {
        rootCauses.push({
          category: 'Extrusion Duplication',
          rootCause: 'Connector walls are being duplicated during extrusion creation or modification operations',
          evidenceCount: tooManyConnectors,
          affectedFloors: diagnostics.filter(d => d.results.some(r => r.issue.includes('Too many connector'))).map(d => d.floorNumber),
          preventionStrategy: 'Add duplicate prevention logic during extrusion creation. Validate connector count before committing operations.',
          urgency: 'high'
        });
      }
    }
    
    // 2. WALL DUPLICATE ISSUES
    const duplicateWallIssues = diagnostics.flatMap(d => 
      d.results.filter(r => r.category === 'walls' && r.issue.includes('Duplicate'))
    );
    
    if (duplicateWallIssues.length > 0) {
      const duplicateConnectors = duplicateWallIssues.filter(i => i.issue.includes('connector')).length;
      
      if (duplicateConnectors > 0) {
        rootCauses.push({
          category: 'Connector Wall Duplication',
          rootCause: 'Connector walls are being created multiple times for the same extrusion, likely during split/resize operations',
          evidenceCount: duplicateConnectors,
          affectedFloors: diagnostics.filter(d => d.results.some(r => r.issue.includes('Duplicate connector'))).map(d => d.floorNumber),
          preventionStrategy: 'Implement connector existence checks before creation. Add transaction-like operations for extrusion modifications.',
          urgency: 'critical'
        });
      }
    }
    
    // 3. ROOM-WALL MEMBERSHIP ISSUES
    const roomWallIssues = diagnostics.flatMap(d => 
      d.results.filter(r => r.category === 'rooms')
    );
    
    if (roomWallIssues.length > 0) {
      rootCauses.push({
        category: 'Room-Wall Relationship Corruption',
        rootCause: 'Room-wall associations are being lost during complex operations, causing rooms to disappear from calculations',
        evidenceCount: roomWallIssues.length,
        affectedFloors: diagnostics.filter(d => d.results.some(r => r.category === 'rooms')).map(d => d.floorNumber),
        preventionStrategy: 'Implement defensive room-wall relationship tracking. Add relationship validation after all operations.',
        urgency: 'high'
      });
    }
    
    // 4. GEOMETRY PRECISION ISSUES
    const geometryIssues = diagnostics.flatMap(d => 
      d.results.filter(r => r.category === 'geometry')
    );
    
    if (geometryIssues.length > 0) {
      rootCauses.push({
        category: 'Coordinate Precision Drift',
        rootCause: 'Significant floating-point precision errors (>0.01mm drift) in wall coordinates that escaped automatic repair',
        evidenceCount: geometryIssues.length,
        affectedFloors: diagnostics.filter(d => d.results.some(r => r.category === 'geometry')).map(d => d.floorNumber),
        preventionStrategy: 'Automatic repair system already in place. These are residual issues that require manual investigation.',
        urgency: 'low'
      });
    }
    
    // 5. WALL IDENTITY ISSUES
    const identityIssues = diagnostics.flatMap(d => 
      d.results.filter(r => r.issue.includes('tempId'))
    );
    
    if (identityIssues.length > 0) {
      rootCauses.push({
        category: 'Wall Identity Corruption',
        rootCause: 'Wall tempIds are being lost or duplicated, breaking tracking systems that depend on wall identity',
        evidenceCount: identityIssues.length,
        affectedFloors: diagnostics.filter(d => d.results.some(r => r.issue.includes('tempId'))).map(d => d.floorNumber),
        preventionStrategy: 'Implement robust wall identity preservation. Add tempId validation and regeneration logic.',
        urgency: 'high'
      });
    }
    
    return rootCauses.sort((a, b) => {
      const urgencyOrder = { critical: 4, high: 3, medium: 2, low: 1 };
      return urgencyOrder[b.urgency] - urgencyOrder[a.urgency];
    });
  };

  const logComprehensiveSummary = (diagnostics: FloorPlanDiagnostics[], rootCauses: RootCauseAnalysis[]) => {
    console.log('\n' + '='.repeat(80));
    console.log('🔍 COMPREHENSIVE FLOOR PLAN ANALYSIS SUMMARY');
    console.log('='.repeat(80));
    
    const totalIssues = diagnostics.reduce((sum, d) => sum + d.totalIssues, 0);
    const totalCritical = diagnostics.reduce((sum, d) => sum + d.criticalIssues, 0);
    
    console.log(`\n📊 OVERVIEW:`);
    console.log(`   • Floors Analyzed: ${diagnostics.length}`);
    console.log(`   • Total Issues: ${totalIssues}`);
    console.log(`   • Critical Issues: ${totalCritical}`);
    console.log(`   • Root Causes Identified: ${rootCauses.length}`);
    
    console.log(`\n🏢 PER-FLOOR BREAKDOWN:`);
    diagnostics.forEach(d => {
      console.log(`   Floor ${d.floorNumber}: ${d.totalIssues} issues (${d.criticalIssues} critical)`);
      console.log(`      • Walls: ${d.summary.walls} | Rooms: ${d.summary.rooms}`);
      console.log(`      • Extrusions: ${d.summary.extrusions} | Connectors: ${d.summary.connectors}`);
      console.log(`      • Problems: Duplicates=${d.summary.duplicateConnectors}, Orphaned=${d.summary.orphanedWalls}`);
    });
    
    console.log(`\n🚨 ROOT CAUSE ANALYSIS:`);
    rootCauses.forEach((cause, index) => {
      const urgencyIcon = { critical: '🔥', high: '⚠️', medium: '⚡', low: 'ℹ️' }[cause.urgency];
      console.log(`\n   ${index + 1}. ${urgencyIcon} ${cause.category} (${cause.urgency.toUpperCase()})`);
      console.log(`      Root Cause: ${cause.rootCause}`);
      console.log(`      Evidence: ${cause.evidenceCount} instances across floors ${cause.affectedFloors.join(', ')}`);
      console.log(`      Prevention: ${cause.preventionStrategy}`);
    });
    
    console.log(`\n💡 RECOMMENDED ACTION PLAN:`);
    console.log(`   1. Address CRITICAL issues first (${rootCauses.filter(r => r.urgency === 'critical').length} items)`);
    console.log(`   2. Focus on extrusion metadata preservation systems`);
    console.log(`   3. Implement defensive validation checks`);
    console.log(`   4. Add comprehensive operation logging for future debugging`);
    
    console.log('\n' + '='.repeat(80));
  };

  const getFloorNumbers = () => {
    const floorNumbers = Object.keys(floorPlans).map(Number).sort();
    if (!floorNumbers.includes(currentFloor)) {
      floorNumbers.push(currentFloor);
    }
    return floorNumbers.sort((a, b) => a - b);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          🔍 Floor Plan Issue Analysis
        </h3>
        <div className="text-sm text-gray-500">
          Diagnostics Only - No Automated Fixes
        </div>
      </div>

      <div className="space-y-4">
        {/* Action Button */}
        <div className="flex gap-2">
          <button
            onClick={runFullSystemAnalysis}
            disabled={isRunningDiagnostics}
            className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white px-6 py-2 rounded font-medium"
          >
            {isRunningDiagnostics ? '🔄 Analyzing...' : '🔍 Analyze All Floors'}
          </button>
          
          {allDiagnostics.length > 0 && (
            <button
              onClick={() => setShowDetailedReport(!showDetailedReport)}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded font-medium"
            >
              {showDetailedReport ? 'Hide Details' : 'Show Technical Details'}
            </button>
          )}
        </div>

        {/* Analysis Results */}
        {allDiagnostics.length > 0 && (
          <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="border border-blue-200 rounded p-3 bg-blue-50">
                <h4 className="font-medium text-blue-900 mb-2">System Overview</h4>
                <div className="text-sm space-y-1">
                  <div>Floors: {allDiagnostics.length}</div>
                  <div>Total Issues: {allDiagnostics.reduce((sum, d) => sum + d.totalIssues, 0)}</div>
                  <div className="text-red-600 font-medium">
                    Critical: {allDiagnostics.reduce((sum, d) => sum + d.criticalIssues, 0)}
                  </div>
                </div>
              </div>
              
              <div className="border border-orange-200 rounded p-3 bg-orange-50">
                <h4 className="font-medium text-orange-900 mb-2">Root Causes Found</h4>
                <div className="text-sm space-y-1">
                  <div>Issues Identified: {rootCauseAnalysis.length}</div>
                  <div className="text-red-600">Critical: {rootCauseAnalysis.filter(r => r.urgency === 'critical').length}</div>
                  <div className="text-orange-600">High Priority: {rootCauseAnalysis.filter(r => r.urgency === 'high').length}</div>
                </div>
              </div>
              
              <div className="border border-green-200 rounded p-3 bg-green-50">
                <h4 className="font-medium text-green-900 mb-2">Most Problematic</h4>
                {(() => {
                  const mostProblematic = allDiagnostics.reduce((prev, current) => 
                    current.criticalIssues > prev.criticalIssues ? current : prev
                  );
                  return (
                    <div className="text-sm space-y-1">
                      <div>Floor: {mostProblematic.floorNumber}</div>
                      <div>Critical Issues: {mostProblematic.criticalIssues}</div>
                      <div>Focus Area: Extrusions</div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Root Cause Analysis */}
            <div className="border border-gray-200 rounded p-4 bg-gray-50">
              <h4 className="font-medium text-gray-900 mb-3">🚨 Root Cause Analysis</h4>
              
              {rootCauseAnalysis.length === 0 ? (
                <div className="text-gray-600">No significant patterns detected.</div>
              ) : (
                <div className="space-y-3">
                  {rootCauseAnalysis.map((cause, index) => (
                    <div key={index} className={`border rounded p-3 ${
                      cause.urgency === 'critical' ? 'border-red-200 bg-red-50' :
                      cause.urgency === 'high' ? 'border-orange-200 bg-orange-50' :
                      cause.urgency === 'medium' ? 'border-yellow-200 bg-yellow-50' :
                      'border-blue-200 bg-blue-50'
                    }`}>
                      <div className="flex items-start justify-between mb-2">
                        <h5 className={`font-medium ${
                          cause.urgency === 'critical' ? 'text-red-900' :
                          cause.urgency === 'high' ? 'text-orange-900' :
                          cause.urgency === 'medium' ? 'text-yellow-900' :
                          'text-blue-900'
                        }`}>
                          {cause.urgency === 'critical' ? '🔥' : cause.urgency === 'high' ? '⚠️' : cause.urgency === 'medium' ? '⚡' : 'ℹ️'} {cause.category}
                        </h5>
                        <span className={`text-xs px-2 py-1 rounded font-medium ${
                          cause.urgency === 'critical' ? 'bg-red-200 text-red-800' :
                          cause.urgency === 'high' ? 'bg-orange-200 text-orange-800' :
                          cause.urgency === 'medium' ? 'bg-yellow-200 text-yellow-800' :
                          'bg-blue-200 text-blue-800'
                        }`}>
                          {cause.urgency.toUpperCase()}
                        </span>
                      </div>
                      
                      <div className="text-sm space-y-2">
                        <p><strong>Root Cause:</strong> {cause.rootCause}</p>
                        <p><strong>Evidence:</strong> {cause.evidenceCount} instances across floors {cause.affectedFloors.join(', ')}</p>
                        <p><strong>Prevention Strategy:</strong> {cause.preventionStrategy}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Floor-by-Floor Breakdown */}
            <div className="border border-gray-200 rounded p-4">
              <h4 className="font-medium text-gray-900 mb-3">🏢 Floor-by-Floor Analysis</h4>
              
              <div className="space-y-2">
                {allDiagnostics.map((floorDiag) => (
                  <div key={floorDiag.floorNumber} className="border rounded p-3 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-medium">Floor {floorDiag.floorNumber}</h5>
                      <div className="flex gap-2 text-sm">
                        <span>Issues: {floorDiag.totalIssues}</span>
                        {floorDiag.criticalIssues > 0 && (
                          <span className="text-red-600 font-medium">Critical: {floorDiag.criticalIssues}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                      <div>Walls: {floorDiag.summary.walls}</div>
                      <div>Extrusions: {floorDiag.summary.extrusions}</div>
                      <div>Connectors: {floorDiag.summary.connectors}</div>
                      <div>Duplicates: {floorDiag.summary.duplicateConnectors}</div>
                    </div>
                    
                    {selectedFloorForAnalysis === floorDiag.floorNumber && (
                      <div className="mt-3 pt-3 border-t">
                        <pre className="text-xs bg-white p-2 rounded overflow-x-auto whitespace-pre-wrap">
                          {generateDiagnosticReport(floorDiag)}
                        </pre>
                      </div>
                    )}
                    
                    <button
                      onClick={() => setSelectedFloorForAnalysis(
                        selectedFloorForAnalysis === floorDiag.floorNumber ? null : floorDiag.floorNumber
                      )}
                      className="mt-2 text-blue-500 hover:text-blue-700 text-sm"
                    >
                      {selectedFloorForAnalysis === floorDiag.floorNumber ? 'Hide' : 'Show'} Technical Details
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Technical Report */}
            {showDetailedReport && (
              <div className="border border-gray-200 rounded p-4">
                <h4 className="font-medium text-gray-900 mb-3">🔧 Complete Technical Report</h4>
                <div className="bg-gray-100 p-3 rounded text-xs overflow-x-auto">
                  <pre className="whitespace-pre-wrap">
                    {allDiagnostics.map(d => generateDiagnosticReport(d)).join('\n\n')}
                  </pre>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Initial State */}
        {allDiagnostics.length === 0 && !isRunningDiagnostics && (
          <div className="text-center py-8 text-gray-500">
            <div className="mb-4">
              <div className="text-4xl mb-2">🔍</div>
              <h4 className="text-lg font-medium text-gray-700 mb-2">Ready to Analyze</h4>
              <p>This tool will identify the root causes of your multi-floor extrusion issues.</p>
            </div>
            
            <div className="text-sm text-left max-w-md mx-auto space-y-2">
              <div><strong>What it detects:</strong></div>
              <ul className="list-disc list-inside space-y-1 text-gray-600">
                <li>Extrusion metadata corruption patterns</li>
                <li>Duplicate connector wall creation</li>
                <li>Room-wall relationship failures</li>
                <li>Coordinate precision drift issues</li>
                <li>Wall identity tracking problems</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FloorPlanDiagnosticsOnly;
